/**
 * Findings UI Constants
 *
 * Color and style constants for severity levels and finding statuses.
 * Used by finding cards, badges, and summary components.
 *
 * Each config uses `labelKey` for i18n translation. The `label` field
 * provides an English fallback. For translated labels in React components,
 * use the `useSeverityConfig()` / `useStatusConfig()` hooks instead.
 */

import { useTranslation } from "@/packages/utils";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SeverityConfig = {
  bg: string;
  border: string;
  dot: string;
  labelKey: string;
  /** English fallback — for translated label, use `useSeverityConfig()` */
  label: string;
};

export type StatusConfig = {
  bg: string;
  text: string;
  labelKey: string;
  /** English fallback — for translated label, use `useStatusConfig()` */
  label: string;
};

// ─── Base Config (static, non-reactive) ──────────────────────────────────────

export const SEVERITY_COLORS: Record<SeverityType, SeverityConfig> = {
  critical: {
    bg: "#fef2f2",
    border: "#ef4444",
    dot: "#ef4444",
    labelKey: "findings.severity.critical",
    label: "Critical",
  },
  major: {
    bg: "#fff7ed",
    border: "#E8734A",
    dot: "#E8734A",
    labelKey: "findings.severity.major",
    label: "Major",
  },
  minor: {
    bg: "#fefce8",
    border: "#eab308",
    dot: "#eab308",
    labelKey: "findings.severity.minor",
    label: "Minor",
  },
  observation: {
    bg: "#f9fafb",
    border: "#6C6C70",
    dot: "#6C6C70",
    labelKey: "findings.severity.observation",
    label: "Observation",
  },
};

export const SEVERITY_CARD_TINTS: Record<SeverityType, string> = {
  critical: "rgba(239,68,68,0.03)",
  major: "rgba(232,115,74,0.03)",
  minor: "rgba(234,179,8,0.03)",
  observation: "rgba(108,108,112,0.03)",
};

export const SEVERITY_ORDER: SeverityType[] = [
  "critical",
  "major",
  "minor",
  "observation",
];

/**
 * Severity options for select dropdowns in finding forms.
 * Used by ManualFindingForm and FindingInlineEditor.
 */
export const SEVERITY_OPTIONS: Array<{
  value: SeverityType;
  labelKey: string;
  label: string;
}> = [
  {
    value: "critical",
    labelKey: "findings.severity.critical",
    label: "Critical",
  },
  { value: "major", labelKey: "findings.severity.major", label: "Major" },
  { value: "minor", labelKey: "findings.severity.minor", label: "Minor" },
  {
    value: "observation",
    labelKey: "findings.severity.observation",
    label: "Observation",
  },
];

export const STATUS_STYLES: Record<FindingStatusType, StatusConfig> = {
  draft: {
    bg: "#f3f4f6",
    text: "#6b7280",
    labelKey: "findings.status.draft",
    label: "Draft",
  },
  open: {
    bg: "#dbeafe",
    text: "#1d4ed8",
    labelKey: "findings.status.open",
    label: "Open",
  },
  resolved: {
    bg: "#dcfce7",
    text: "#16a34a",
    labelKey: "findings.status.resolved",
    label: "Resolved",
  },
};

// ─── Hooks (translated labels) ───────────────────────────────────────────────

/**
 * Hook that returns severity color config with translated labels.
 * Mirrors the pattern from ChecklistRow's `useChecklistStatusConfig()`.
 */
export function useSeverityConfig(): Record<SeverityType, SeverityConfig> {
  const { t } = useTranslation("audits");
  return {
    critical: {
      ...SEVERITY_COLORS.critical,
      label: t(
        SEVERITY_COLORS.critical.labelKey as "findings.severity.critical",
      ) as string,
    },
    major: {
      ...SEVERITY_COLORS.major,
      label: t(
        SEVERITY_COLORS.major.labelKey as "findings.severity.major",
      ) as string,
    },
    minor: {
      ...SEVERITY_COLORS.minor,
      label: t(
        SEVERITY_COLORS.minor.labelKey as "findings.severity.minor",
      ) as string,
    },
    observation: {
      ...SEVERITY_COLORS.observation,
      label: t(
        SEVERITY_COLORS.observation.labelKey as "findings.severity.observation",
      ) as string,
    },
  };
}

/**
 * Hook that returns status style config with translated labels.
 */
export function useStatusConfig(): Record<FindingStatusType, StatusConfig> {
  const { t } = useTranslation("audits");
  return {
    draft: {
      ...STATUS_STYLES.draft,
      label: t(
        STATUS_STYLES.draft.labelKey as "findings.status.draft",
      ) as string,
    },
    open: {
      ...STATUS_STYLES.open,
      label: t(STATUS_STYLES.open.labelKey as "findings.status.open") as string,
    },
    resolved: {
      ...STATUS_STYLES.resolved,
      label: t(
        STATUS_STYLES.resolved.labelKey as "findings.status.resolved",
      ) as string,
    },
  };
}

/**
 * Hook that returns severity options with translated labels for dropdowns.
 */
export function useSeverityOptions(): Array<{
  value: SeverityType;
  label: string;
}> {
  const { t } = useTranslation("audits");
  return SEVERITY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey as "findings.severity.critical") as string,
  }));
}

/**
 * Hook that returns status filter options with translated labels.
 */
export function useStatusOptions(): Array<{
  value: FindingStatusType;
  label: string;
}> {
  const { t } = useTranslation("audits");
  return [
    {
      value: "open",
      label: t(STATUS_STYLES.open.labelKey as "findings.status.open") as string,
    },
    {
      value: "resolved",
      label: t(
        STATUS_STYLES.resolved.labelKey as "findings.status.resolved",
      ) as string,
    },
  ];
}
